import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import moment from 'moment';
import { catchError, firstValueFrom, map, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CapacitorStorageService } from '../lib/capacitor-storage.service';
import { LocalStorageService } from '../lib/local-storage.service';
import { LoginResponse, Advertisment } from '../model/login-response.dto';
import * as AppActions from './../store/actions/app.actions';
import * as ReservationListActions from './../store/actions/reservation-list.actions';
import * as AppSelectors from './../store/selectors/app.selectors';
import { NgxSpinnerService } from 'ngx-spinner';
import { UtilsService } from '../lib/utils.service';

@Injectable({
  providedIn: 'root'
})
export class AuthApiService {

  constructor(
    private httpClient: HttpClient,
    private localStorageService: LocalStorageService,
    private capacitorStorageService: CapacitorStorageService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private utilsService: UtilsService,
    private store: Store<{ restaurantId: number, date: Date }>
  ) { }

  private baseUrl = `${environment.apiUrl}/employees`;
  private callbacks: (() => void)[] = [];

  restaurantId$: Observable<number> = this.store.pipe(select(AppSelectors.selectRestaurantId));

  onLogin(cb: () => void) {
    this.callbacks.push(cb);
    if (this.localStorageService.isAuthenticated()) {
      cb();
    }
  }

  logIn(username: string, password: string, workingDate: Date): Observable<LoginResponse> {
    return this.httpClient.post<LoginResponse>(`${this.baseUrl}/logIn`, { username, password, workingDate }).pipe(
      map((res: LoginResponse) => {
        if (res.Status == false) {
          throw res;
        }

        this.loggedIn(res, true);
        return res;
      })
    );
  }

  logout(restaurantId: string) {
    this.utilsService.cancelPendingRequests();
    
    return this.httpClient.post<LoginResponse>(`${this.baseUrl}/logOut`, { restaurantId }).pipe(
      map((res: any) => {
      }),
      catchError(this.handleError)
    );
  }

  logInWithoutCredentials(employeeId: string, restaurantId: string, workingDate: Date): Observable<LoginResponse> {
    return this.httpClient.post<LoginResponse>(`${this.baseUrl}/logIn`, { employeeId, restaurantId, username: null, password: null, workingDate: "2022-04-22" }).pipe(
      map((res: LoginResponse) => {
        if (res.Status == false) {
          throw res;
        }

        this.loggedIn(res, false);
        return res;
      })
    );
  }
  
  loadAds(restaurantId: string) {              
      if (restaurantId == null) return;       
      
      fetch(`${environment.adsDataUrl}?t=${new Date().getTime()}`)
      .then(response => response.json())
      .then(async (ads: Advertisment[]) => {
        if (ads == null) return null; 
        
        const adsForThis = ads.find((x: Advertisment) => x.excluded.indexOf(restaurantId) == -1 && 
                                                          (x.restaurants.indexOf(restaurantId) > -1 || x.restaurants.indexOf("ALL") > -1));          
        
        const prevAds = await this.capacitorStorageService.getAds();
        const adsHaveChanged = (prevAds == null && adsForThis != null) ||
                                (prevAds != null && adsForThis == null) || 
                                (adsForThis?.name != prevAds?.name);

        if (adsHaveChanged === true) {
          this.capacitorStorageService.setAdsNextDisplayDayTime(null);
          this.capacitorStorageService.setAdsTimesDisplayed(null);
        }

        this.capacitorStorageService.setAds(adsForThis ?? null);

        this.store.dispatch({ type: AppActions.setAds.type, ads: adsForThis });

        return adsForThis;
      });      
  }

  async notifyForAdsInterest() {    
    const ads = await this.capacitorStorageService.getAds();
    const data = localStorage.getItem("userData");
    const dataParsed = data == null ? null : JSON.parse(data);

    const restName = encodeURIComponent(dataParsed.restaurantsList[0].title);
    const adsName = encodeURIComponent(ads?.name ?? "-");
    const restId = encodeURIComponent(dataParsed.restaurantId);
    const emplName = encodeURIComponent(`${dataParsed.loggedInemployee.surname} ${dataParsed.loggedInemployee.name} (${dataParsed.loggedInemployee.type}`);    

    const subj = `New Advertisment Interest Expressed - ${restName}`;
    const txt = `Ads Campaign: ${adsName}, Restaurant: ${restName} (${restId}), Employee: ${emplName})`;

    // this request will fail with CORS error but the notification will be sent!
    fetch(`${environment.webServerUrl}/home/GenericContact?txt=${txt}&subj=${subj}&channel=POCKET`)
    .then(response => response.json())
    .then((resp) => {
      console.log(resp); 
    });      
  }

  private loggedIn(res: LoginResponse, resetDate: boolean) {
    this.localStorageService.saveUserData(res);
    this.localStorageService.saveRestaurantId(res?.restaurantId);
    this.localStorageService.setRefreshInterval(res.restaurantsList);
    this.localStorageService.saveOnlineCategories(res.restaurantsList);
    this.localStorageService.saveHasOnlineReservations(res.restaurantsList);
    this.localStorageService.saveExtraStatuses(res.restaurantsList);
    this.localStorageService.saveMenus(res.restaurantsList);
    this.localStorageService.saveCommunication(res.restaurantsList);
    this.localStorageService.saveServiceStopDate(res.restaurantsList);
    this.localStorageService.saveMinTelephoneLength(res.restaurantsList);

    const rest = res?.restaurantsList?.find(r => r.restaurantId == res?.restaurantId);
    this.localStorageService.saveCustomerTypes(rest != null ? rest.customerTypes : []);

    this.store.dispatch({ type: AppActions.setRestaurantId.type, restaurantId: parseInt(res?.restaurantId) });
    this.store.dispatch({ type: AppActions.setMenus.type, menus: this.localStorageService.getMenus() });
    if (resetDate) {
      this.store.dispatch({ type: ReservationListActions.setDate.type, date: moment(new Date()).format('yyyy-MM-DD') });
    }
    this.store.dispatch({ type: AppActions.loadSources.type });
    this.store.dispatch({ type: AppActions.setCommunication.type, communication: this.localStorageService.getCommunication() });
    this.store.dispatch({ type: AppActions.setServiceStopDate.type, stopDate: this.localStorageService.getServiceStopDate() });

    this.loadAds(res?.restaurantId);

    // reset explicit logout flag
    this.capacitorStorageService.setExplicitLogout(false);

    for (const cb of this.callbacks) {
      cb();
    }
  }

  async changeRestaurant(restaurantId: string) {
    if (this.utilsService.getRequestIsInProgress() === true) {
      setTimeout(() => { this.changeRestaurant(restaurantId); }, 250);
      console.log("request was in progress!");
      return;
    }

    this.spinner.show();
    this.utilsService.setChangeRestaurantRequested(true);
    this.store.dispatch({ type: AppActions.setQueueHttpRequests.type, value: true });

    const data = this.localStorageService.getUserData();
    const account = data?.loggedInemployee?.chainAcounts?.find(a => a.restaurantId === restaurantId);
    if (account == null) {     
      this.spinner.hide();
      this.store.dispatch({ type: AppActions.setQueueHttpRequests.type, value: false });
      this.utilsService.setChangeRestaurantRequested(false);
      console.log("weird, account is null");

      return;
    }

    const date = await firstValueFrom(this.store.select(s => s.date));
    const activeRestaurantId = await firstValueFrom(this.restaurantId$);

    try {
      await firstValueFrom(this.logout(activeRestaurantId.toString()));
      this.store.dispatch({ type: AppActions.logout.type, dontRestState: true });
      this.capacitorStorageService.clearButLeaveCredentials();
    } catch (e) {
      this.spinner.hide();      
      this.store.dispatch({ type: AppActions.setQueueHttpRequests.type, value: false });
      this.utilsService.setChangeRestaurantRequested(false);
      console.log("failed to logout", e);

      return;
    }

    try {
      await firstValueFrom(this.logInWithoutCredentials(account.employeeId, account.restaurantId, date));
    } catch (e) {
      this.spinner.hide();
      this.store.dispatch({ type: AppActions.setQueueHttpRequests.type, value: false });
      this.utilsService.setChangeRestaurantRequested(false);
      console.log("failed to login", e);

      return;
    }

    setTimeout(() => {      
      window.location.reload();  
      this.store.dispatch({ type: AppActions.setQueueHttpRequests.type, value: true }); 
      this.utilsService.setChangeRestaurantRequested(false);
      //this.spinner.hide();   
    }, 1000);
  }

  private handleError(error: HttpErrorResponse) {    
    this.router.navigate(["/auth/login"]);
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }
}
